import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';

import { fmtDay, fmtDayShort } from '@/lib/datetime';

import './performance-trend-chart.css';

export const PERFORMANCE_TREND_METRICS = [
  'gross_spend',
  'impressions',
  'clicks',
  'ctr',
  'cpm',
  'cpc',
  'attributed_conversions',
  'attributed_revenue',
  'cpa',
  'roas',
] as const;

export type PerformanceTrendMetric = typeof PERFORMANCE_TREND_METRICS[number];

/**
 * One ET calendar day of customer-safe Performance facts.
 *
 * Attribution fields are intentionally limited to customer-safe,
 * assigned-production facts.
 */
export interface DirectPerformanceTrendPoint {
  date: string;
  deliveryAvailable: boolean;
  directAttributionAvailable: boolean;
  grossSpendCents: number;
  impressions: number;
  clicks: number;
  attributionGrossSpendCents: number | null;
  directConversions: number;
  directRevenueCents: number;
}

export interface PerformanceTrendChartProps {
  points: ReadonlyArray<DirectPerformanceTrendPoint>;
  metric: PerformanceTrendMetric;
  ariaLabel?: string;
  className?: string;
}

export interface PerformanceTrendYAxis {
  min: number;
  max: number;
  /** Values are ordered from the top of the chart to the bottom. */
  tickValues: number[];
}

interface MetricDefinition {
  label: string;
  shortLabel: string;
  value: (point: DirectPerformanceTrendPoint) => number | null;
  format: (value: number, compact?: boolean) => string;
}

const integerFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});
const decimalFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const compactNumberFormat = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compactCurrencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const DEFAULT_AXIS_INTERVALS = 4;
const MAX_HUNDRED_DOLLAR_AXIS_INTERVALS = 6;
const HUNDRED_DOLLARS_IN_CENTS = 10_000;
const HUNDRED_DOLLAR_AXIS_METRICS: ReadonlySet<PerformanceTrendMetric> = new Set([
  'gross_spend',
  'attributed_revenue',
]);

function finite(value: number): number | null {
  return Number.isFinite(value) ? value : null;
}

function deliveryValue(
  point: DirectPerformanceTrendPoint,
  value: number | null,
): number | null {
  return point.deliveryAvailable && value !== null ? finite(value) : null;
}

function attributionValue(
  point: DirectPerformanceTrendPoint,
  value: number | null,
): number | null {
  return point.directAttributionAvailable && value !== null ? finite(value) : null;
}

function combinedValue(
  point: DirectPerformanceTrendPoint,
  value: () => number | null,
): number | null {
  if (!point.deliveryAvailable || !point.directAttributionAvailable) return null;
  const result = value();
  return result === null ? null : finite(result);
}

function ratio(numerator: number, denominator: number, multiplier = 1): number | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return null;
  }
  return numerator / denominator * multiplier;
}

function formatCount(value: number, compact = false): string {
  if (compact) return compactNumberFormat.format(value);
  return Math.abs(value - Math.round(value)) < 0.000001
    ? integerFormat.format(value)
    : decimalFormat.format(value);
}

function formatCurrencyCents(value: number, compact = false): string {
  return (compact ? compactCurrencyFormat : currencyFormat).format(value / 100);
}

function formatPercent(value: number): string {
  return `${decimalFormat.format(value)}%`;
}

function formatRoas(value: number): string {
  return `${decimalFormat.format(value)}×`;
}

const METRICS: Record<PerformanceTrendMetric, MetricDefinition> = {
  gross_spend: {
    label: 'Gross spend',
    shortLabel: 'Spend',
    value: (point) => deliveryValue(point, point.grossSpendCents),
    format: formatCurrencyCents,
  },
  impressions: {
    label: 'Impressions',
    shortLabel: 'Impressions',
    value: (point) => deliveryValue(point, point.impressions),
    format: formatCount,
  },
  clicks: {
    label: 'Clicks',
    shortLabel: 'Clicks',
    value: (point) => deliveryValue(point, point.clicks),
    format: formatCount,
  },
  ctr: {
    label: 'Click-through rate',
    shortLabel: 'CTR',
    value: (point) => deliveryValue(
      point,
      ratio(point.clicks, point.impressions, 100),
    ),
    format: formatPercent,
  },
  cpm: {
    label: 'Cost per thousand impressions',
    shortLabel: 'CPM',
    value: (point) => deliveryValue(
      point,
      ratio(point.grossSpendCents, point.impressions, 1_000),
    ),
    format: formatCurrencyCents,
  },
  cpc: {
    label: 'Cost per click',
    shortLabel: 'CPC',
    value: (point) => deliveryValue(
      point,
      ratio(point.grossSpendCents, point.clicks),
    ),
    format: formatCurrencyCents,
  },
  attributed_conversions: {
    label: 'Tickets',
    shortLabel: 'Tickets',
    value: (point) => attributionValue(point, point.directConversions),
    format: formatCount,
  },
  attributed_revenue: {
    label: 'Ticket Value',
    shortLabel: 'Ticket Value',
    value: (point) => attributionValue(point, point.directRevenueCents),
    format: formatCurrencyCents,
  },
  cpa: {
    label: 'Cost per ticket',
    shortLabel: 'CPA',
    value: (point) => combinedValue(
      point,
      () => point.attributionGrossSpendCents === null
        ? null
        : ratio(point.attributionGrossSpendCents, point.directConversions),
    ),
    format: formatCurrencyCents,
  },
  roas: {
    label: 'Return on ad spend',
    shortLabel: 'ROAS',
    value: (point) => combinedValue(
      point,
      () => point.attributionGrossSpendCents === null
        ? null
        : ratio(point.directRevenueCents, point.attributionGrossSpendCents),
    ),
    format: formatRoas,
  },
};

function metricValue(
  metric: PerformanceTrendMetric,
  point: DirectPerformanceTrendPoint,
): number | null {
  return METRICS[metric].value(point);
}

function shortDate(date: string, includesMultipleYears: boolean): string {
  return includesMultipleYears ? fmtDay(date) : fmtDayShort(date);
}

/** Responsive sparse ticks; the first and last calendar days are invariant. */
export function performanceTrendTickIndexes(count: number, width: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const tickCount = Math.min(count, Math.max(2, Math.min(7, Math.floor(width / 105))));
  const indexes = new Set<number>([0, count - 1]);
  for (let tick = 1; tick < tickCount - 1; tick += 1) {
    indexes.add(Math.round(tick * (count - 1) / (tickCount - 1)));
  }
  return [...indexes].sort((left, right) => left - right);
}

/**
 * Calculates the visible Y domain and its labels.
 *
 * Absolute dollar totals use outward, $100-aligned bounds. Their step may be
 * a larger whole multiple of $100 so a large range never floods the chart
 * with labels. Unit costs intentionally retain the original proportional
 * scale: rounding a $3 CPC up to $100 would make the series unreadable.
 */
export function performanceTrendYAxis(
  metric: PerformanceTrendMetric,
  values: ReadonlyArray<number | null>,
): PerformanceTrendYAxis {
  const finiteValues = values.filter((value): value is number => (
    value !== null && Number.isFinite(value)
  ));
  const rawMin = finiteValues.length ? Math.min(0, ...finiteValues) : 0;
  const rawMax = finiteValues.length ? Math.max(0, ...finiteValues) : 1;

  if (!HUNDRED_DOLLAR_AXIS_METRICS.has(metric)) {
    const rawSpan = Math.max(rawMax - rawMin, 1);
    let min = rawMin < 0 ? rawMin - rawSpan * 0.1 : 0;
    let max = rawMax > 0 ? rawMax + rawSpan * 0.1 : 0;
    if (min === max) {
      min = 0;
      max = 1;
    }
    const span = max - min;
    return {
      min,
      max,
      tickValues: Array.from(
        { length: DEFAULT_AXIS_INTERVALS + 1 },
        (_, index) => max - span * index / DEFAULT_AXIS_INTERVALS,
      ),
    };
  }

  if (rawMin === 0 && rawMax === 0) {
    return {
      min: 0,
      max: HUNDRED_DOLLARS_IN_CENTS,
      tickValues: [HUNDRED_DOLLARS_IN_CENTS, 0],
    };
  }

  const spanInHundreds = (rawMax - rawMin) / HUNDRED_DOLLARS_IN_CENTS;
  let stepInHundreds = Math.max(
    1,
    Math.ceil(spanInHundreds / MAX_HUNDRED_DOLLAR_AXIS_INTERVALS),
  );

  for (;;) {
    const step = stepInHundreds * HUNDRED_DOLLARS_IN_CENTS;
    const min = Math.floor(rawMin / step) * step;
    const max = Math.ceil(rawMax / step) * step;
    const intervalCount = Math.round((max - min) / step);

    if (intervalCount <= MAX_HUNDRED_DOLLAR_AXIS_INTERVALS) {
      return {
        min: Object.is(min, -0) ? 0 : min,
        max,
        tickValues: Array.from(
          { length: intervalCount + 1 },
          (_, index) => max - step * index,
        ),
      };
    }

    // Outward rounding can add an interval on each side of a signed range.
    // Grow proportionally so even extreme corrections resolve in one or two
    // iterations instead of walking upward in $100 increments.
    stepInHundreds = Math.max(
      stepInHundreds + 1,
      Math.ceil(
        stepInHundreds * intervalCount / MAX_HUNDRED_DOLLAR_AXIS_INTERVALS,
      ),
    );
  }
}

function lineSegments(
  values: ReadonlyArray<number | null>,
  x: (index: number) => number,
  y: (value: number) => number,
): Array<Array<readonly [number, number]>> {
  const result: Array<Array<readonly [number, number]>> = [];
  let segment: Array<readonly [number, number]> = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (segment.length) result.push(segment);
      segment = [];
      return;
    }
    segment.push([x(index), y(value)]);
  });
  if (segment.length) result.push(segment);
  return result;
}

function useChartWidth() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(760);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const measure = () => {
      const next = Math.round(container.getBoundingClientRect().width);
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return { containerRef, width };
}

function findNextAvailable(
  values: ReadonlyArray<number | null>,
  from: number,
  direction: -1 | 1,
): number {
  for (
    let index = from + direction;
    index >= 0 && index < values.length;
    index += direction
  ) {
    if (values[index] !== null) return index;
  }
  return from;
}

function findBoundaryAvailable(
  values: ReadonlyArray<number | null>,
  direction: -1 | 1,
): number | null {
  let index = direction === 1 ? 0 : values.length - 1;
  while (index >= 0 && index < values.length) {
    if (values[index] !== null) return index;
    index += direction;
  }
  return null;
}

function TooltipMetric({
  metric,
  point,
  selected,
}: {
  metric: PerformanceTrendMetric;
  point: DirectPerformanceTrendPoint;
  selected: boolean;
}) {
  const definition = METRICS[metric];
  const value = definition.value(point);
  return (
    <div
      className={`performance-trend-chart__tooltip-metric${selected ? ' is-selected' : ''}`}
      data-metric={metric}
      data-selected={selected ? 'true' : 'false'}
    >
      <span>{definition.shortLabel}</span>
      <strong>{value === null ? 'Unavailable' : definition.format(value)}</strong>
    </div>
  );
}

export function PerformanceTrendChart({
  points,
  metric,
  ariaLabel = 'Ticket performance trend',
  className = '',
}: PerformanceTrendChartProps) {
  const titleId = useId();
  const descriptionId = useId();
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLElement>(null);
  const { containerRef, width } = useChartWidth();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const orderedPoints = useMemo(
    () => [...points].sort((left, right) => left.date.localeCompare(right.date)),
    [points],
  );
  const values = useMemo(
    () => orderedPoints.map((point) => metricValue(metric, point)),
    [metric, orderedPoints],
  );

  useEffect(() => {
    setActiveIndex((current) => (
      current !== null && values[current] !== undefined && values[current] !== null
        ? current
        : null
    ));
  }, [values]);

  const height = 260;
  const pad = { top: 20, right: 18, bottom: 42, left: width < 500 ? 48 : 58 };
  const innerWidth = Math.max(width - pad.left - pad.right, 1);
  const innerHeight = height - pad.top - pad.bottom;
  const finiteValues = values.filter((value): value is number => value !== null);
  const { min, max, tickValues } = performanceTrendYAxis(metric, values);
  const span = max - min;
  const step = innerWidth / Math.max(orderedPoints.length - 1, 1);
  const x = (index: number) => orderedPoints.length === 1
    ? pad.left + innerWidth / 2
    : pad.left + index * step;
  const y = (value: number) => pad.top + (max - value) / span * innerHeight;
  const zeroY = y(0);
  const segments = lineSegments(values, x, y);
  const tickIndexes = performanceTrendTickIndexes(orderedPoints.length, width);
  const firstYear = orderedPoints[0]?.date.slice(0, 4);
  const lastYear = orderedPoints[orderedPoints.length - 1]?.date.slice(0, 4);
  const includesMultipleYears = firstYear !== lastYear;
  const activePoint = activeIndex === null ? null : orderedPoints[activeIndex] ?? null;
  const activeValue = activeIndex === null ? null : values[activeIndex] ?? null;
  const selectedDefinition = METRICS[metric];

  const selectPoint = (index: number) => {
    if (values[index] !== null && values[index] !== undefined) setActiveIndex(index);
  };

  const handleKeyDown = (
    event: ReactKeyboardEvent<SVGGElement>,
    index: number,
  ) => {
    let next: number | null = null;
    if (event.key === 'ArrowLeft') next = findNextAvailable(values, index, -1);
    if (event.key === 'ArrowRight') next = findNextAvailable(values, index, 1);
    if (event.key === 'Home') next = findBoundaryAvailable(values, 1);
    if (event.key === 'End') next = findBoundaryAvailable(values, -1);
    if (event.key === 'Escape') {
      setActiveIndex(null);
      event.currentTarget.blur();
      event.preventDefault();
      return;
    }
    if (next === null) return;
    event.preventDefault();
    const target = wrapperRef.current?.querySelector<SVGGElement>(
      `[data-performance-point-index="${next}"]`,
    );
    target?.focus();
    selectPoint(next);
  };

  const handlePointerLeave = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return;
    if (wrapperRef.current?.contains(document.activeElement)) return;
    setActiveIndex(null);
  };

  const handleBlur = (event: ReactFocusEvent<HTMLElement>) => {
    if (event.relatedTarget && wrapperRef.current?.contains(event.relatedTarget as Node)) return;
    setActiveIndex(null);
  };

  const tooltipStyle = activeIndex === null
    ? undefined
    : ({
        '--performance-trend-tooltip-x': `${x(activeIndex) / width * 100}%`,
      } as CSSProperties);

  return (
    <figure
      ref={wrapperRef}
      className={`performance-trend-chart ${className}`.trim()}
      aria-label={ariaLabel}
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
    >
      <div className="performance-trend-chart__surface" ref={containerRef}>
        <svg
          className="performance-trend-chart__svg"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="group"
          aria-labelledby={`${titleId} ${descriptionId}`}
          preserveAspectRatio="none"
        >
          <title id={titleId}>{selectedDefinition.label} over time</title>
          <desc id={descriptionId}>
            ET calendar-day trend. Unpublished days are shown as gaps. Use Tab and arrow keys to inspect published points.
          </desc>

          <g className="performance-trend-chart__grid" aria-hidden="true">
            {tickValues.map((tickValue) => {
              const tickY = y(tickValue);
              return (
                <g key={tickValue} data-y-axis-tick={tickValue}>
                  <line x1={pad.left} x2={width - pad.right} y1={tickY} y2={tickY}/>
                  <text x={pad.left - 8} y={tickY + 3} textAnchor="end">
                    {selectedDefinition.format(tickValue, true)}
                  </text>
                </g>
              );
            })}
          </g>

          {min < 0 && max > 0 && (
            <line
              className="performance-trend-chart__zero-line"
              x1={pad.left}
              x2={width - pad.right}
              y1={zeroY}
              y2={zeroY}
              aria-hidden="true"
            />
          )}

          <g className="performance-trend-chart__x-axis" aria-hidden="true">
            {tickIndexes.map((index) => (
              <g key={orderedPoints[index]!.date}>
                <line x1={x(index)} x2={x(index)} y1={height - pad.bottom} y2={height - pad.bottom + 5}/>
                <text
                  x={x(index)}
                  y={height - 16}
                  textAnchor={orderedPoints.length === 1
                    ? 'middle'
                    : index === 0
                      ? 'start'
                      : index === orderedPoints.length - 1
                        ? 'end'
                        : 'middle'}
                >
                  {shortDate(orderedPoints[index]!.date, includesMultipleYears)}
                </text>
              </g>
            ))}
          </g>

          <g className="performance-trend-chart__series" aria-hidden="true">
            {segments.map((segment, index) => {
              const path = segment.map(([pointX, pointY], pointIndex) => (
                `${pointIndex === 0 ? 'M' : 'L'}${pointX},${pointY}`
              )).join(' ');
              if (segment.length === 1) {
                const [pointX, pointY] = segment[0]!;
                return (
                  <circle
                    key={index}
                    data-series-segment={index}
                    cx={pointX}
                    cy={pointY}
                    r="2.5"
                  />
                );
              }
              const first = segment[0]!;
              const last = segment[segment.length - 1]!;
              return (
                <g key={index} data-series-segment={index}>
                  <path
                    className="performance-trend-chart__area"
                    d={`${path} L${last[0]},${zeroY} L${first[0]},${zeroY} Z`}
                  />
                  <path className="performance-trend-chart__line" d={path}/>
                </g>
              );
            })}
            {values.map((value, index) => value === 0 && (
              <circle
                key={orderedPoints[index]!.date}
                className="performance-trend-chart__confirmed-zero"
                data-confirmed-zero={orderedPoints[index]!.date}
                cx={x(index)}
                cy={zeroY}
                r="3"
              />
            ))}
          </g>

          {activePoint && activeValue !== null && activeIndex !== null && (
            <g className="performance-trend-chart__selection" aria-hidden="true">
              <line x1={x(activeIndex)} x2={x(activeIndex)} y1={pad.top} y2={height - pad.bottom}/>
              <circle
                data-active-performance-point={activePoint.date}
                data-value={activeValue}
                cx={x(activeIndex)}
                cy={y(activeValue)}
                r="4.5"
              />
            </g>
          )}

          <g className="performance-trend-chart__hit-targets">
            {orderedPoints.map((point, index) => {
              const value = values[index] ?? null;
              const left = index === 0 ? pad.left : (x(index - 1) + x(index)) / 2;
              const right = index === orderedPoints.length - 1
                ? width - pad.right
                : (x(index) + x(index + 1)) / 2;
              const isActive = activeIndex === index;
              return (
                <g
                  key={`${point.date}-${index}`}
                  role="button"
                  tabIndex={value === null ? -1 : 0}
                  aria-disabled={value === null ? 'true' : undefined}
                  aria-pressed={isActive}
                  aria-describedby={isActive ? tooltipId : undefined}
                  aria-keyshortcuts="ArrowLeft ArrowRight Home End Escape"
                  aria-label={value === null
                    ? `${fmtDay(point.date)}. ${selectedDefinition.label} unavailable.`
                    : `${fmtDay(point.date)}. ${selectedDefinition.label}: ${selectedDefinition.format(value)}.`}
                  data-performance-point-index={index}
                  data-performance-point-date={point.date}
                  onFocus={() => selectPoint(index)}
                  onPointerEnter={() => selectPoint(index)}
                  onPointerMove={() => selectPoint(index)}
                  onPointerDown={(event) => {
                    selectPoint(index);
                    if (event.pointerType !== 'touch') event.currentTarget.focus();
                  }}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                >
                  <rect
                    x={left}
                    y={pad.top}
                    width={Math.max(right - left, 1)}
                    height={innerHeight}
                  />
                </g>
              );
            })}
          </g>
        </svg>

        {!finiteValues.length && (
          <div className="performance-trend-chart__empty" role="status">
            No published days for {selectedDefinition.shortLabel} in this window.
          </div>
        )}

        {activePoint && activeValue !== null && (
          <div
            id={tooltipId}
            className="performance-trend-chart__tooltip"
            data-side={activeIndex !== null && activeIndex / Math.max(orderedPoints.length - 1, 1) > 0.55 ? 'left' : 'right'}
            style={tooltipStyle}
            role="tooltip"
            aria-live="polite"
          >
            <div className="performance-trend-chart__tooltip-heading">
              <strong>{fmtDay(activePoint.date)}</strong>
              <span>Direct attribution only</span>
            </div>
            <div className="performance-trend-chart__tooltip-grid">
              {PERFORMANCE_TREND_METRICS.map((tooltipMetric) => (
                <TooltipMetric
                  key={tooltipMetric}
                  metric={tooltipMetric}
                  point={activePoint}
                  selected={tooltipMetric === metric}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <figcaption className="sr-only">
        {selectedDefinition.label} by ET calendar day. Confirmed zero values remain connected; unavailable days break the line.
      </figcaption>
    </figure>
  );
}
